import { Fragment, useEffect, useRef, useState } from "react";
import type { Settings } from "../types";
import { guestCopy } from "../lib/guest-i18n";
import { guestServices } from "../lib/guest-services";
import { guestActionCopy } from "../lib/guest-actions";
import { GuestRequestFlow, type GuestRequestType } from "./GuestRequestFlow";
import { GuestServiceIcon } from "./GuestServiceIcon";
import { GuestActionIcon } from "./GuestActionIcon";
import { InventoryDeck } from "./InventoryDeck";
import type { ReservationContact } from "./VehicleCardDetails";
import { loadPublicInventory, type InventoryVehicle } from "../lib/accountApi";
import { toolText } from "../lib/toolCopy";
import { PrecisionIcon } from "./PrecisionIcon";

type Language = Settings["language"];
const STOCK_URL = "https://www.precisionautotechnik.com/cars",
  SHOP_URL = "https://www.precisionautotechnik.com/category/ev-parts",
  MAP_URL = "https://maps.google.com/?q=Wattwerkstrasse+2,+4416+Bubendorf";
export function GuestLanding({
  language,
  apiBase,
  onSignIn,
  onCreateAccount,
  authenticated = false,
  contact,
  onAddVehicle,
  onMenu,
  onTools,
}: {
  language: Language;
  apiBase: string;
  onSignIn: () => void;
  onCreateAccount: () => void;
  authenticated?: boolean;
  contact?: ReservationContact;
  onAddVehicle?: () => void;
  onMenu?: () => void;
  onTools?: () => void;
}) {
  const c = guestCopy[language],
    actionCopy = guestActionCopy[language],
    services = guestServices(language),
    rail = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const [stock, setStock] = useState<InventoryVehicle[]>([]);
  const [request, setRequest] = useState<{
    type: GuestRequestType;
    service?: string;
  } | null>(null);
  const hero = [
    {
      image: "./guest-workshop-v1.jpg",
      action: () => setRequest({ type: "APPOINTMENT" }),
    },
    { image: "./guest-vehicles-v1.jpg", href: STOCK_URL },
    {
      image: "./guest-parts-v1.jpg",
      action: () => setRequest({ type: "EV_CHECK", service: c.categories[6] }),
    },
  ];
  useEffect(() => {
    const timer = window.setInterval(
      () => setActive((value) => (value + 1) % hero.length),
      6500,
    );
    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    rail.current?.scrollTo({
      left: active * rail.current.clientWidth,
      behavior: "smooth",
    });
  }, [active]);
  useEffect(() => {
    let mounted = true;
    void loadPublicInventory(apiBase).then((items) => { if (mounted) setStock(items); }).catch(() => undefined);
    return () => { mounted = false; };
  }, [apiBase]);
  const actions: [GuestRequestType, string][] = [
    ["APPOINTMENT", "./action-appointment-v1.jpg"],
    ["QUOTE", "./action-quote-v1.jpg"],
    ["EV_CHECK", "./action-ev-check-v1.jpg"],
    ["ASSISTANCE", "./action-assistance-v1.jpg"],
  ];
  return (
    <main className={`guest-landing${authenticated ? " guest-landing--account" : ""}`}>
      <section className="guest-hero guest-hero--compact">
        <header>
          {authenticated && (
            <button
              className="guest-add-vehicle"
              type="button"
              onClick={onAddVehicle}
            >
              Adicionar veículo
            </button>
          )}
          <img src="./precision-logo.png" alt="Precision Autotechnik" />
          {authenticated && (
            <button
              className="guest-account-menu"
              type="button"
              onClick={onMenu}
              aria-label="Menu"
            >
              <span aria-hidden="true">☰</span>
            </button>
          )}
          {!authenticated && (
            <button type="button" onClick={onSignIn}>
              {c.signIn}
            </button>
          )}
        </header>
        <div
          className="guest-slide-rail"
          ref={rail}
          onScroll={() => {
            const el = rail.current;
            if (el) setActive(Math.round(el.scrollLeft / el.clientWidth));
          }}
        >
          {hero.map((slide, index) => {
            const text = c.slides[index];
            return (
              <article
                className="guest-slide"
                key={slide.image}
                style={{
                  backgroundImage: `linear-gradient(180deg,rgba(0,0,0,.14),rgba(5,7,10,.22) 45%,#08090b 100%),url(${slide.image})`,
                }}
              >
                <div className="guest-slide-copy">
                  <h1>{text[0]}</h1>
                  <p>{text[1]}</p>
                  {slide.href ? (
                    <a href={slide.href} target="_blank" rel="noreferrer">
                      {text[2]}
                    </a>
                  ) : (
                    <button type="button" onClick={slide.action}>
                      {text[2]}
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
        <nav className="guest-slide-dots" aria-label="Slides">
          {hero.map((_, i) => (
            <button
              type="button"
              key={i}
              className={i === active ? "active" : ""}
              onClick={() => setActive(i)}
              aria-label={`${i + 1}`}
            />
          ))}
        </nav>
      </section>
      <section className="guest-section guest-action-section">
        <div className="guest-heading guest-action-heading">
          <span>PRECISION CARE</span>
          <h2>{actionCopy.title}</h2>
          <p>{actionCopy.intro}</p>
        </div>
        <div className="guest-action-carousel">
          {actions.map(([type, image], index) => (
            <Fragment key={type}>
            <button
              type="button"
              key={type}
              className={`guest-action-card guest-action-card--${type.toLowerCase()}`}
              style={{
                backgroundImage: `linear-gradient(180deg,rgba(2,5,9,.08),rgba(2,5,9,.42) 42%,rgba(2,5,9,.96) 100%),url(${image})`,
              }}
              onClick={() =>
                setRequest({
                  type,
                  service: type === "EV_CHECK" ? c.categories[6] : undefined,
                })
              }
            >
              <span>
                <GuestActionIcon type={type} />
              </span>
              <div>
                <strong>{actionCopy.actions[index][0]}</strong>
                <small>{actionCopy.actions[index][1]}</small>
              </div>
              <i>›</i>
            </button>
            {index === 0 && onTools && (
              <button
                type="button"
                className="guest-action-card guest-action-card--tools"
                data-native-i18n
                style={{
                  backgroundImage: "linear-gradient(180deg,rgba(2,5,9,.08),rgba(2,5,9,.25) 42%,rgba(2,5,9,.96) 100%),url(./action-tools-v1.jpg)",
                }}
                onClick={onTools}
              >
                <span><PrecisionIcon name="grid" /></span>
                <div>
                  <strong>{toolText(language, "tools")}</strong>
                  <small>{toolText(language, "intro")}</small>
                  <small>{toolText(language, "public")}</small>
                </div>
                <i aria-hidden="true">›</i>
              </button>
            )}
            </Fragment>
          ))}
        </div>
      </section>
      <section className="guest-section guest-services">
        <div className="guest-heading">
          <h2>{c.servicesTitle}</h2>
          <p>{c.servicesIntro}</p>
        </div>
        <div className="guest-card-rail">
          {services.map((service) => (
            <article
              className="guest-service-card"
              key={service.title}
              style={{
                backgroundImage: `linear-gradient(180deg,rgba(2,5,9,.12),rgba(2,5,9,.45) 35%,rgba(2,5,9,.96) 100%),url(${service.image})`,
              }}
            >
              <div className="guest-service-card__icon">
                <GuestServiceIcon name={service.icon} />
              </div>
              <div className="guest-service-card__content">
                <h3>{service.title}</h3>
                <p>{service.summary}</p>
                <ul>
                  {service.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() =>
                    setRequest({
                      type: service.evCheck ? "EV_CHECK" : "APPOINTMENT",
                      service: service.title,
                    })
                  }
                >
                  {service.evCheck ? c.evCta : c.book}
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>
      <section className="guest-section guest-vehicles">
        <InventoryDeck vehicles={stock} language={language} apiBase={apiBase} contact={authenticated?contact:undefined} embedded title={c.vehiclesTitle} onClose={()=>{}} />
      </section>
      <section
        className="guest-shop"
        style={{
          backgroundImage:
            "linear-gradient(90deg,rgba(3,5,8,.94),rgba(3,5,8,.38)),url(./guest-parts-v1.jpg)",
        }}
      >
        <div>
          <h2>{c.shopTitle}</h2>
          <p>{c.shopText}</p>
          <a href={SHOP_URL} target="_blank" rel="noreferrer">
            {c.shopCta}
          </a>
        </div>
      </section>
      <section className="guest-section guest-company">
        <div className="guest-company-head">
          <img src="./precision-logo-black.png" alt="Precision Autotechnik" />
          <h2>{c.companyTitle}</h2>
        </div>
        <div className="guest-company-grid">
          <article>
            <b>{c.location}</b>
            <p>
              Wattwerkstrasse 2<br />
              CH-4416 Bubendorf
            </p>
            <a href={MAP_URL} target="_blank" rel="noreferrer">
              {c.directions} ↗
            </a>
          </article>
          <article>
            <b>{c.hours}</b>
            <p>
              Mo.–Fr. 07:30–12:00
              <br />
              13:00–17:30
              <br />
              Sa. 09:00–12:00
              <br />
              So. geschlossen
            </p>
          </article>
          <article>
            <b>{c.contact}</b>
            <p>
              <a href="tel:+41619313719">+41 61 931 37 19</a>
              <br />
              <a href="tel:+41782228575">WhatsApp +41 78 222 85 75</a>
              <br />
              <a href="mailto:info@precisionautotechnik.com">
                info@precisionautotechnik.com
              </a>
            </p>
          </article>
        </div>
      </section>
      <footer className="guest-footer">
        Precision Autotechnik AG · Bubendorf
      </footer>
      {request && (
        <GuestRequestFlow
          language={language}
          apiBase={apiBase}
          requestType={request.type}
          presetService={request.service}
          onClose={() => setRequest(null)}
          onCreateAccount={onCreateAccount}
        />
      )}
    </main>
  );
}
